import { gql, useQuery } from '@apollo/client';
import { ActivityType } from 'components/common/DropdownFilters/ActivityTypeDropdownFilter';
import { Tab } from 'components/common/NavTabsLayout';
import { useMemo } from 'react';
import { EventCourse } from 'services/graphqltypes';

const LOAD_COMMUNITY_ACTIVITIES = gql`
  query loadCommunityActivities(
    $communitySlug: String!
    $endTime: DatetimeFilter
    $isPublished: Boolean
    $isUnlisted: Boolean
    $type: BestPracticeType
    $isEventFilterValue: Boolean
    $isCourseFilterValue: Boolean
    $orderBy: [EventCoursesOrderBy!]
    $count: Int
    $offset: Int
  ) {
    eventCourses(
      first: $count
      offset: $offset
      orderBy: $orderBy
      filter: {
        endTime: $endTime
        isPublished: { equalTo: $isPublished }
        isUnlisted: { equalTo: $isUnlisted }
        type: { equalTo: $type }
        or: [
          { isCourse: { equalTo: $isCourseFilterValue } },
          { isEvent: { equalTo: $isEventFilterValue } }
        ]
        and: {
          or: [
            { communityByOrganizerCommunity: { slug: { equalTo: $communitySlug } } }
            {
              communityByOrganizerCommunity: {
                parentCommunity: { slug: { equalTo: $communitySlug } } # also load all school class events
              }
            }
          ]
        }
      }
    ) {
      totalCount
      nodes {
        id
        name
        type
        slug
        totalCredits
        pictureUrl
        startTime
        communityByOrganizerCommunity {
          id
          name
          slug
        }
        locationName
        locationAddress
        locationCoordinates { x, y }
        locationGeojson
      }
    }
    communityBySlug(slug: $communitySlug) {
      id
      name
    }
  }
`;

const useLoadCommunityActivities = ({
  communitySlug, timeScope, state, type, orderBy, count, offset
}: {
  /** Slug of the community whose activities to load */
  communitySlug: string,
  /** Period of time: upcoming, past, all */
  timeScope: 'upcoming' | 'past' | 'all',
  /** state: draft (contains both public and private drafts) | published | private */
  state: Tab,
  /** Type: event | course | route | all */
  type: ActivityType,
  /** Ordering of results: 'START_TIME_ASC' | 'START_TIME_DESC' */
  orderBy?: 'START_TIME_ASC' | 'START_TIME_DESC' | 'UPDATED_AT_DESC',
  /** Number of results to be returned */
  count?: number,
  /** Offset: skip n records (used for pagination) */
  offset?: number,
}) => {

  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const endTime = (timeScope === 'all')
    ? null
    : (timeScope === 'upcoming')
      ? { greaterThanOrEqualTo: now.toISOString() }
      : { lessThan: now.toISOString() };

  // default sorting (if orderBy not passed)
  const defaultOrderBy =
    state === 'drafts'
      ? 'UPDATED_AT_DESC' // drafts: last edited on the top
      : timeScope === 'past'
        ? 'START_TIME_DESC' // past events: the ones that happened most recently on the top
        : 'START_TIME_ASC'; // other: from oldest to newest

  const { loading, data, error, refetch } = useQuery(LOAD_COMMUNITY_ACTIVITIES, {
    variables: {
      communitySlug: communitySlug,
      count: count,
      offset: offset,
      endTime: endTime,
      type: (type === 'routes') ? 'ROUTE' : null, // can not use now; changes in storing logic needed
      isEventFilterValue: (type === 'events') || (type === 'routes') || (type === 'all')
        ? true
        : undefined,
      isCourseFilterValue: (type === 'courses') || (type === 'all')
        ? true
        : undefined,
      isPublished: state === 'published' || state === 'private',
      isUnlisted: state === 'published' ? false : state === 'private' ? true : null,
      orderBy: orderBy || defaultOrderBy,
    }
  });

  // array of activities
  const activities: Array<EventCourse> = useMemo(() => {
    return data?.eventCourses?.nodes;
  }, [data]);

  // totalCount
  const totalCount = data?.eventCourses?.totalCount as number;

  // community name
  const communityName = data?.communityBySlug?.name as object;

  return { loading, error, refetch, data, activities, totalCount, communityName };
};

export default useLoadCommunityActivities;
